'use strict';

/**
 * SiteMap -- the one navigation graph for the whole app, consumed by pict-section-navigation. It
 * drives the docked main sidebar (the non-settings groups), the settings sub-rail (the Settings
 * group, scoped), and the cmd+K launcher (everything). Groups are top-level categories; their
 * Children are the navigable items. Each item carries a Route (a hash URL) the app navigates to.
 *
 * Group hashes are set explicitly so the host can scope a render to a subset of groups without
 * worrying about slug collisions; the Settings group's children are left unhashed so the normalizer
 * prefixes them (settings.profile, ...), which keeps the settings items from colliding with the
 * top-level ones. The app routes settings items by parsing their Route, not their hash.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

// The groups shown in the docked main sidebar. The You group (personal pages) and the Settings
// group are excluded on purpose: You backs the cmd+K launcher only, and Settings backs the in-page
// settings sub-rail (the gear) and the launcher. Both are still in the graph so the launcher lists
// them.
const MAIN_GROUP_HASHES = ['workspace', 'plan', 'design', 'work', 'deliver', 'fleet', 'tools'];
const SETTINGS_GROUP_HASH = 'settings-area';
// The docked sidebar shows the main groups PLUS the Settings group (a collapsible group at the
// bottom), so settings pages live in the same shared rail and turn up in the sidebar search. The
// hide-filter and hideableNavItems() stay scoped to MAIN_GROUP_HASHES so settings items are never
// offered for hiding -- only the main nav is hideable.
const DOCKED_GROUP_HASHES = MAIN_GROUP_HASHES.concat([SETTINGS_GROUP_HASH]);

/**
 * Build the navigation graph.
 * @param {function(string):boolean} fCan - entitlement check ( (entKey) => boolean ); items with an
 *        Ent they cannot do are dropped. Pass through-true to include everything.
 * @param {object} pLex - the resolved Drafting Kit lexicon (renames labels), or null.
 * @param {Array<string>} pHidden - item Hashes the plan sheet owner hid from the sidebar; dropped
 *        everywhere the graph drives (sidebar, settings rail, launcher). A main group that loses all
 *        of its children is dropped too. Only items in hideableNavItems() are ever honored here.
 * @returns {Array<object>}
 */
function buildSiteMap(fCan, pLex, pHidden)
{
	let tmpCan = (typeof fCan === 'function') ? fCan : function () { return true; };
	let tmpHide = new Set(Array.isArray(pHidden) ? pHidden : []);
	let tmpMain = new Set(MAIN_GROUP_HASHES);
	// Resolve a nav label from the plan sheet's Drafting Kit lexicon (plural form),
	// falling back to the canonical default when no kit renames it.
	let fLabel = (pTerm, pDefault) => (pLex && pLex[pTerm] && pLex[pTerm].Plural) ? pLex[pTerm].Plural : pDefault;
	// Resolve the secondary nav blurb the same way, so a renamed item gets its own
	// description instead of inheriting the stock one (e.g. "Read-throughs" need not
	// say "Executions of routines, step by step.").
	let fDesc = (pTerm, pDefault) => (pLex && pLex[pTerm] && pLex[pTerm].Description) ? pLex[pTerm].Description : pDefault;

	let tmpSettingsChildren =
	[
		{ Name: 'Profile', Route: '#/settings/profile', Icon: 'IdCard', Description: 'Your name, title, and color.' },
		{ Name: 'About Me', Route: '#/settings/about', Icon: 'Feather', Description: 'A rich profile others can read.' },
		{ Name: 'Appearance', Route: '#/settings/appearance', Icon: 'Palette', Description: 'Theme, color mode, and text size.' },
		{ Name: 'Sign-in', Route: '#/settings/account', Icon: 'Key', Description: 'Change your email or password.' },
		{ Name: 'Notifications', Route: '#/settings/notifications', Icon: 'Bell', Description: 'How the platform reaches you: email and text alerts.' },
		{ Name: 'API Tokens', Route: '#/settings/tokens', Icon: 'Terminal', Description: 'Tokens for the CLI and automation.' },
		{ Name: 'Connections', Route: '#/settings/connections', Icon: 'Robot', Description: 'Connect Claude, and control which plan sheets each connection may act in.' },
		{ Name: 'Plan sheets', Route: '#/settings/plansheets', Icon: 'Box', Description: 'Your memberships and default plan sheet.' }
	];
	let tmpAdminChildren =
	[
		{ Name: 'Team', Route: '#/settings/team', Icon: 'Users', Ent: 'users.manage', Description: 'Members and their roles.' },
		{ Name: 'Roles', Route: '#/settings/roles', Icon: 'UserShield', Ent: 'roles.manage', Description: 'Custom roles and what they can do.' },
		{ Name: 'Org Settings', Route: '#/settings/org', Icon: 'Building', Ent: 'platform.manage', Description: 'Organization profile.' },
		{ Name: 'Top Bar', Route: '#/settings/topbar', Icon: 'Image', Ent: 'content.edit', Description: "The art across this plan sheet's top bar." },
		{ Name: 'Drafting Kits', Route: '#/settings/draftingkit', Icon: 'DraftingCompass', Ent: 'platform.manage', Description: 'Build and manage your crafts.' },
		{ Name: 'Documentation Sync', Route: '#/settings/docsync', Icon: 'Book', Ent: 'content.edit', Description: 'Connect a GitHub repo and sync its docs.' },
		{ Name: 'Stickers', Route: '#/settings/stickers', Icon: 'Shapes', Ent: 'stickers.manage', Description: 'Stickers the team can place on moodboards.' },
		{ Name: 'Workflow', Route: '#/workflow', Icon: 'Network', Ent: 'users.manage', Description: 'Configure the workflow an administrator sets for the platform.' },
		{ Name: 'Prompt Shapes', Route: '#/prompt-shapes', Icon: 'PenRuler', Ent: 'users.manage', Description: 'The prompt shapes the organization crafts against: adopt platform defaults or author fresh.' },
		{ Name: 'Tasks', Route: '#/tasks', Icon: 'Sparkles', Ent: 'content.edit', Description: 'The user-facing tasks your people can run: name one, bind a prompt shape and a node capability, and it appears in Ask.' },
		{ Name: 'Activity', Route: '#/org/activity', Icon: 'Rss', Ent: 'users.manage', Description: 'What your team has been doing.' },
		{ Name: 'Ops Console', Route: '#/admin/ops', Icon: 'HeartPulse', Ent: 'platform.manage', Description: 'Errors, activity, and who is active now.' }
	].filter((pItem) => tmpCan(pItem.Ent));
	// Deliver: the customer-facing side of the work -- what is owed, who it is for, and what they said.
	// The build-and-ship machinery moved out to Fleet below.
	let tmpDeliverChildren =
	[
		{ Name: 'Deliver', Hash: 'deliver-board', Route: '#/deliver', Icon: 'Truck', Description: 'Move deliverables from scoped to accepted.' },
		{ Name: 'Expectations', Hash: 'expectations', Route: '#/expectations', Icon: 'HandHelping', Description: 'The high-level deliverables a customer expects.' },
		{ Name: 'Customers', Hash: 'keystone-customers', Route: '#/keystone-customers', Icon: 'Handshake', Description: 'The customers you serve and what they tell you.' },
		{ Name: 'Call Desk', Hash: 'call-desk', Route: '#/call-desk', Icon: 'Headset', Description: 'Log calls with customers and turn them into issues.' },
		{ Name: 'Issues', Hash: 'issues', Route: '#/issues', Icon: 'Warning', Description: 'Bugs, defects, and customer-reported problems.' },
		{ Name: fLabel('Feedback', 'Feedback'), Hash: 'feedback', Route: '#/feedback', Icon: 'CommentDots', Description: fDesc('Feedback', 'What testers and customers said.') }
	].filter((pItem) => tmpCan(pItem.Ent));

	// Fleet: the agents that build and ship, and the machinery around them. These three sat under Deliver
	// for a while, but they answer a different question. Deliver is about how the ORG delivers -- what a
	// customer expects, what they told you, what you owe them. Fleet is about the AGENTS doing the work:
	// which node is running what, what it produced, and what ships where. Someone checking on a customer
	// commitment and someone checking on a node are not on the same errand.
	let tmpFleetChildren =
	[
		{ Name: 'Releases', Hash: 'releases', Route: '#/releases', Icon: 'Rocket', Ent: 'platform.manage', Description: 'Deploy to dev and promote validated As-Builts to prod.' },
		// Runs is deliberately NOT gated on platform.manage: anyone on the team should be able to watch a
		// run and read the evidence a node attached. Only the Approve and Reject controls are gated, and
		// the server enforces that with content.approve. It is also why this group is never empty for a
		// non-admin -- Releases and Nodes filter out, Runs does not, so the group never renders headless.
		{ Name: 'Runs', Hash: 'runs', Route: '#/runs', Icon: 'Robot', Description: 'Toolchain runs a node is executing, and the steps waiting on your approval.' },
		// Nodes: the registered agent nodes and their capability. Gated on platform.manage like Releases, because
		// suspending a node is an operational act; the Suspend / Resume controls are additionally content.approve.
		{ Name: 'Nodes', Hash: 'node-registrations', Route: '#/node-registrations', Icon: 'Box', Ent: 'platform.manage', Description: 'Registered agent nodes, what each can do, and whether it is Active or Suspended.' },
		{ Name: 'Capabilities', Hash: 'capabilities', Route: '#/capabilities', Icon: 'Bolt', Ent: 'content.approve', Description: 'Of the nodes that advertise each capability, choose which one is live.' }
	].filter((pItem) => tmpCan(pItem.Ent));

	let tmpGraph =
	[
		{
			Name: 'Workspace', Hash: 'workspace', Icon: 'Columns', Blurb: 'Where the work lives.',
			Children:
			[
				{ Name: 'Board', Hash: 'board', Route: '#/board', Icon: 'Columns', Description: 'Work items on a kanban board.' },
				// A board of its own, because work items move at agent speed and features move at
				// product speed. One surface implied they should move together, and that implication
				// is the pressure that produces rubber stamps.
				{ Name: 'Feature Board', Hash: 'feature-board', Route: '#/feature-board', Icon: 'Star', Description: 'Features on their own board, and where their promise is accepted.' },
				{ Name: 'System Activity', Hash: 'system-activity', Route: '#/system-activity', Icon: 'HeartPulse', Description: 'How much work and knowledge the team creates over time.' },
				{ Name: 'Community', Hash: 'community', Route: '#/community', Icon: 'Users', Description: 'Everyone on the team.' }
			]
		},
		{
			Name: 'Plan', Hash: 'plan', Icon: 'ListChecks', Blurb: 'What you are building.',
			Children:
			[
				{ Name: fLabel('Vision', 'Visions'), Hash: 'visions', Route: '#/visions', Icon: 'Eye', Description: fDesc('Vision', 'What you are building and why.') },
				{ Name: fLabel('Module', 'Modules'), Hash: 'modules', Route: '#/modules', Icon: 'Box', Description: fDesc('Module', 'The parts of the system.') },
				{ Name: fLabel('Feature', 'Features'), Hash: 'features', Route: '#/features', Icon: 'Star', Description: fDesc('Feature', 'What each module does.') }
			]
		},
		{
			Name: 'Design', Hash: 'design', Icon: 'DraftingCompass', Blurb: 'How it should work.',
			Children:
			[
				{ Name: fLabel('Documentation', 'Documentation'), Hash: 'docs', Route: '#/docs', Icon: 'Book', Description: fDesc('Documentation', 'The team library.') },
				{ Name: fLabel('Whiteboard', 'Whiteboards'), Hash: 'whiteboards', Route: '#/whiteboards', Icon: 'Chalkboard', Description: fDesc('Whiteboard', 'A free-form canvas of ideas.') },
				{ Name: 'Blueprints', Hash: 'blueprints', Route: '#/blueprints', Icon: 'Ruler', Description: 'Composed title blocks you reuse.' },
				// The unit a customer is handed, as opposed to a single blueprint: a named group, issued as
				// Rev A and reproducible afterwards. Sits beside Blueprints because that is what it is made of.
				{ Name: fLabel('Routine', 'Routines'), Hash: 'routines', Route: '#/routines', Icon: 'ListOrdered', Description: fDesc('Routine', 'Step-by-step procedures to exercise the product.') },
				{ Name: 'Prompt Library', Hash: 'prompts', Route: '#/prompts', Icon: 'Wand', Description: 'Craft prompts, run them, and share what works.' }
			]
		},
		{
			Name: 'Build', Hash: 'work', Icon: 'Ticket', Blurb: 'The tasks in flight.',
			Children:
			[
				{ Name: fLabel('WorkItem', 'Work Items'), Hash: 'work-items', Route: '#/work-items', Icon: 'Ticket', Description: fDesc('WorkItem', 'The tasks in flight.') },
				{ Name: 'Sessions', Hash: 'sessions', Route: '#/sessions', Icon: 'Comments', Description: 'The Claude sessions behind the work.' },
				{ Name: fLabel('TestRun', 'Test Runs'), Hash: 'test-runs', Route: '#/test-runs', Icon: 'Flask', Description: fDesc('TestRun', 'Executions of routines, step by step.') },
				{ Name: 'As-Builts', Hash: 'as-builts', Route: '#/as-builts', Icon: 'Package', Description: 'What each session delivered, ready to validate and promote.' }
			]
		},
		{
			Name: 'Tools', Hash: 'tools', Icon: 'Toolbox', Blurb: 'Your personal workspace.',
			Children:
			[
				{ Name: 'Ask', Hash: 'ask', Route: '#/ask', Icon: 'Sparkles', Description: 'Run a task your team set up and get an answer back.' },
				{ Name: 'Projects', Hash: 'projects', Route: '#/projects', Icon: 'FolderOpen', Description: 'Build a form, a sticker, or anything from a cluster of files and the tasks you run against it.' },
				{ Name: 'Project Types', Hash: 'project-types', Route: '#/project-types', Icon: 'FileText', Description: 'Reusable project shapes your team designs with the assistant.' },
				{ Name: 'Checklists', Hash: 'checklists', Route: '#/checklists', Icon: 'ListChecks', Description: 'Your private lists, just for you.' },
				{ Name: 'Notes', Hash: 'notes', Route: '#/notes', Icon: 'FileText', Description: 'Private markdown notes by the drafting table.' },
				{ Name: 'Bookmarks', Hash: 'bookmarks', Route: '#/bookmarks', Icon: 'Bookmark', Description: 'Pinned pages and where you have been.' },
				{ Name: 'Gallery', Hash: 'gallery', Route: '#/gallery', Icon: 'Image', Description: 'Your private images and uploads.' },
				{ Name: 'Drawings', Hash: 'drawings', Route: '#/drawings', Icon: 'Pencil', Description: 'Sketch and diagram on a full Excalidraw canvas.' }
			]
		},
		{
			Name: 'Deliver', Hash: 'deliver', Icon: 'Truck', Blurb: 'What you deliver, and the customers you serve.',
			Children: tmpDeliverChildren
		},
		{
			Name: 'Fleet', Hash: 'fleet', Icon: 'Robot', Blurb: 'The agents that build and ship.',
			Children: tmpFleetChildren
		},
		{
			Name: 'You', Hash: 'you', Icon: 'User', Blurb: 'Your space and your settings.',
			Children:
			[
				{ Name: 'Your plan sheets', Hash: 'home', Route: '#/home', Icon: 'House', Description: 'Every plan sheet you belong to, across tenants.' },
				{ Name: 'My Profile', Hash: 'profile', Route: '#/profile', Icon: 'IdBadge', Description: 'Your personal page.' },
				{ Name: 'Settings', Hash: 'settings', Route: '#/settings', Icon: 'Settings', Description: 'Profile, sign-in, team, and tokens.' }
			]
		},
		{
			Name: 'Settings', Hash: SETTINGS_GROUP_HASH, Secondary: true, Collapsed: true, Icon: 'Settings', Blurb: 'Manage your account and team.',
			Children: tmpSettingsChildren.concat(tmpAdminChildren)
		}
	];
	if (tmpHide.size)
	{
		// Only the docked main groups are ever filtered, so a stray hash (a hand-edited row, a
		// renamed item) can never reach into the You / Settings groups and hide the gear.
		tmpGraph = tmpGraph
			.map((pGroup) => tmpMain.has(pGroup.Hash) ? Object.assign({}, pGroup, { Children: (pGroup.Children || []).filter((pItem) => !tmpHide.has(pItem.Hash)) }) : pGroup)
			.filter((pGroup) => (pGroup.Children && pGroup.Children.length) || !tmpMain.has(pGroup.Hash));
	}
	return tmpGraph;
}

// The nav items a plan sheet owner may hide from the sidebar: every leaf in the docked main groups,
// with its current (lexicon-resolved) label and its group, so the Org Settings picker and the write
// sanitizer agree on exactly one catalog. The You / Settings groups are never offered (they back the
// launcher and the always-present gear). Hashes here are the contract honored by buildSiteMap's filter.
function hideableNavItems(pLex)
{
	let tmpMain = new Set(MAIN_GROUP_HASHES);
	let tmpItems = [];
	buildSiteMap(null, pLex || null, null).forEach((pGroup) =>
	{
		if (!tmpMain.has(pGroup.Hash)) { return; }
		(pGroup.Children || []).forEach((pItem) =>
		{
			tmpItems.push({ Hash: pItem.Hash, Name: pItem.Name, Description: pItem.Description || '', GroupHash: pGroup.Hash, GroupName: pGroup.Name });
		});
	});
	return tmpItems;
}

module.exports = { buildSiteMap: buildSiteMap, hideableNavItems: hideableNavItems, MAIN_GROUP_HASHES: MAIN_GROUP_HASHES, DOCKED_GROUP_HASHES: DOCKED_GROUP_HASHES, SETTINGS_GROUP_HASH: SETTINGS_GROUP_HASH };
